import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { StripeEnv } from "@/lib/stripe.server";
import { PRO_PRICE_IDS } from "@/lib/feature-flags";

let _admin: any = null;
function getAdmin() {
  if (!_admin) {
    _admin = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!);
  }
  return _admin;
}

export const getUsage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: StripeEnv }) => {
    if (data?.environment !== "sandbox" && data?.environment !== "live") {
      throw new Error("Invalid environment");
    }
    return data;
  })
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const admin = getAdmin();
    const [{ data: isPremium }, { data: count }, subRes, grantRes] = await Promise.all([
      admin.rpc("has_active_subscription", { user_uuid: userId, check_env: data.environment }),
      admin.rpc("count_analyses_this_month", { user_uuid: userId }),
      admin
        .from("subscriptions")
        .select("price_id, status, current_period_end")
        .eq("user_id", userId)
        .eq("environment", data.environment)
        .in("status", ["active", "trialing"]),
      admin
        .from("access_grants")
        .select("tier, active, revoked_at, code")
        .eq("beneficiary_user_id", userId)
        .eq("active", true),
    ]);
    const subs = (subRes?.data ?? []) as Array<{ price_id: string | null }>;
    const isPro = subs.some((s) => s.price_id && PRO_PRICE_IDS.includes(s.price_id));
    const grants = ((grantRes?.data ?? []) as Array<{
      tier: string;
      revoked_at: string | null;
      code: string;
    }>).filter((g) => !g.revoked_at);
    const grantTier = grants.find((g) => g.tier === "pro")
      ? "pro"
      : grants.find((g) => g.tier === "premium")
      ? "premium"
      : null;
    return {
      isPremium: Boolean(isPremium) || grantTier === "premium" || grantTier === "pro",
      isPro: Boolean(isPro) || grantTier === "pro",
      grantTier,
      grantCode: grants[0]?.code ?? null,
      analysesThisMonth: (count as number) ?? 0,
      freeLimit: 1,
    };
  });